import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { Switch } from '@/components/ui-kit/switch';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Check, ChevronDown, Info, Search, Sparkles, X, Zap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Interface for available AI context keys from previous screens
export interface AvailableContextKey {
  screenTitle: string;
  key: string;
  type: string;
  fullPath: string;
}

interface AISettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Field name/label */
  targetLabel: string;
  /** Field name (for auto-generating key) */
  fieldName: string;
  /** Index/number for display badge */
  targetIndex: number;
  /** Whether AI suggestions are enabled */
  aiEnabled: boolean;
  /** AI suggestion context key */
  aiKey: string;
  /** Callback when AI settings change */
  onSave: (enabled: boolean, key: string) => void;
  /** Available context keys from previous screen enrichments */
  availableContextKeys?: AvailableContextKey[];
}

export function AISettingsDialog({
  open,
  onOpenChange,
  targetLabel,
  fieldName,
  targetIndex,
  aiEnabled: initialAiEnabled,
  aiKey: initialAiKey,
  onSave,
  availableContextKeys = [],
}: AISettingsDialogProps) {
  const { t } = useTranslation();
  // Local state for editing
  const [enabled, setEnabled] = useState(initialAiEnabled);
  const [key, setKey] = useState(initialAiKey);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset local state when dialog opens
  useEffect(() => {
    if (open) {
      setEnabled(initialAiEnabled);
      setKey(initialAiKey);
      setSearchQuery('');
    }
  }, [open, initialAiEnabled, initialAiKey]);

  const handleSave = () => {
    onSave(enabled, key);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const autoGenerateKey = () => {
    // Try to find a matching key from available context keys based on field name
    const fieldNameLower = fieldName.toLowerCase();
    const matchingKey = availableContextKeys.find(
      (k) => k.key.toLowerCase() === fieldNameLower || k.key.toLowerCase().includes(fieldNameLower)
    );

    if (matchingKey) {
      setKey(matchingKey.fullPath);
    } else {
      // Fallback to generating AI_ prefixed key
      const sanitizedName = fieldName.replace(/[^a-zA-Z0-9]/g, '_');
      setKey(`AI${sanitizedName}`);
    }
  };

  // Filter keys based on search
  const filteredAvailableKeys = useMemo(() => {
    if (!searchQuery) return availableContextKeys;
    const query = searchQuery.toLowerCase();
    return availableContextKeys.filter(
      (k) =>
        k.key.toLowerCase().includes(query) ||
        k.fullPath.toLowerCase().includes(query) ||
        k.screenTitle.toLowerCase().includes(query)
    );
  }, [searchQuery, availableContextKeys]);

  // Group keys by screen
  const groupedKeys = useMemo(() => {
    const groups: Record<string, AvailableContextKey[]> = {};
    filteredAvailableKeys.forEach((k) => {
      if (!groups[k.screenTitle]) {
        groups[k.screenTitle] = [];
      }
      groups[k.screenTitle].push(k);
    });
    return groups;
  }, [filteredAvailableKeys]);

  const selectKey = (selectedKey: string) => {
    setKey(selectedKey);
    setPickerOpen(false);
    setSearchQuery('');
  };

  // Check if current key is from available keys
  const currentKeyInfo = useMemo(() => {
    const available = availableContextKeys.find((k) => k.fullPath === key || k.key === key);
    if (available)
      return { type: 'available', label: available.key, screen: available.screenTitle };

    if (key) return { type: 'custom', label: key };
    return null;
  }, [key, availableContextKeys]);

  const hasAvailableKeys = availableContextKeys.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0"
      // showCloseButton={false}
      >
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border bg-linear-to-r from-[hsl(var(--selise-blue))]/5 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleCancel}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-globe-grey" />
              </button>
              <DialogTitle className="flex items-center gap-2 text-base font-medium">
                <Sparkles className="h-4 w-4 text-selise-blue" />
                {t('TEMPLATEBUILDER_AISETTINGSDIALOG_TITLE')}
                <Badge variant="secondary" className="bg-selise-blue/10 text-selise-blue font-mono">
                  #{targetIndex}
                </Badge>
              </DialogTitle>
            </div>
            <button
              onClick={handleCancel}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4 text-globe-grey" />
            </button>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Target Info */}
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="bg-selise-blue/5 border-selise-blue/20 text-selise-blue font-mono"
              >
                #{targetIndex}
              </Badge>
              <span className="text-sm text-[hsl(var(--fg))] truncate">{targetLabel}</span>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border">
              <div className="space-y-1">
                <p className="text-sm font-medium text-[hsl(var(--fg))]">
                  {t('TEMPLATEBUILDER_AISETTINGSDIALOG_ENABLETITLE')}
                </p>
                <p className="text-xs text-globe-grey">
                  {t('TEMPLATEBUILDER_AISETTINGSDIALOG_ENABLEDESCRIPTION')}
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={(checked) => {
                  setEnabled(checked);
                  if (checked && !key && hasAvailableKeys) {
                    autoGenerateKey();
                  }
                }}
              />
            </div>

            {/* AI Configuration (shown when enabled) */}
            <AnimatePresence>
              {enabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-6">
                    {/* Context Key Picker */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-[hsl(var(--fg))]">
                        {t('TEMPLATEBUILDER_AISETTINGSDIALOG_CONTEXTKEYLABEL')}
                      </Label>
                      <p className="text-xs text-globe-grey">
                        {t('TEMPLATEBUILDER_AISETTINGSDIALOG_CONTEXTKEYDESCRIPTION')}
                      </p>

                      {hasAvailableKeys ? (
                        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                'w-full flex items-center justify-between p-3 rounded-xl border transition-colors text-left',
                                key
                                  ? 'border-selise-blue bg-selise-blue/5'
                                  : 'border-border hover:border-selise-blue/50'
                              )}
                            >
                              {key ? (
                                <div className="flex items-center gap-2">
                                  <Sparkles className="h-4 w-4 text-selise-blue" />
                                  <span className="font-mono text-sm text-[hsl(var(--fg))]">
                                    {key}
                                  </span>
                                  {currentKeyInfo?.type === 'available' &&
                                    currentKeyInfo.screen && (
                                      <Badge variant="secondary" className="text-xs">
                                        {currentKeyInfo.screen}
                                      </Badge>
                                    )}
                                </div>
                              ) : (
                                <span className="text-sm text-globe-grey">
                                  {t('TEMPLATEBUILDER_AISETTINGSDIALOG_SELECTCONTEXTKEY')}
                                </span>
                              )}
                              <ChevronDown className="h-4 w-4 text-globe-grey" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-[--radix-popover-trigger-width] p-0"
                            align="start"
                            sideOffset={4}
                          >
                            {/* Search */}
                            <div className="p-3 border-b border-border">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-globe-grey" />
                                <Input
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  placeholder={t(
                                    'TEMPLATEBUILDER_AISETTINGSDIALOG_SEARCHPLACEHOLDER'
                                  )}
                                  className="pl-9"
                                  autoFocus
                                />
                              </div>
                            </div>

                            <div className="max-h-75 overflow-y-auto">
                              {/* Keys grouped by screen */}
                              {Object.entries(groupedKeys).map(([screenTitle, keys]) => (
                                <div
                                  key={screenTitle}
                                  className="p-2 border-b border-border last:border-b-0"
                                >
                                  <p className="px-2 py-1.5 text-xs font-medium text-globe-grey uppercase flex items-center gap-1">
                                    <Sparkles className="h-3 w-3" />
                                    {screenTitle}
                                  </p>
                                  {keys.map((ctx, idx) => (
                                    <button
                                      key={`${screenTitle}-${idx}`}
                                      type="button"
                                      onClick={() => selectKey(ctx.fullPath)}
                                      className={cn(
                                        'w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors',
                                        key === ctx.fullPath
                                          ? 'bg-selise-blue/10 text-selise-blue'
                                          : 'hover:bg-muted'
                                      )}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm">{ctx.key}</span>
                                        {key === ctx.fullPath && (
                                          <Check className="h-4 w-4 text-selise-blue" />
                                        )}
                                      </div>
                                      <span className="text-xs text-globe-grey">{ctx.type}</span>
                                    </button>
                                  ))}
                                </div>
                              ))}

                              {filteredAvailableKeys.length === 0 && searchQuery && (
                                <div className="p-4 text-center text-sm text-globe-grey">
                                  {t('TEMPLATEBUILDER_AISETTINGSDIALOG_NOKEYSMATCH', {
                                    query: searchQuery,
                                  })}
                                </div>
                              )}

                              {/* Auto-generate option */}
                              <div className="p-2 border-t border-border">
                                <div className="flex items-center gap-2 p-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      autoGenerateKey();
                                      setPickerOpen(false);
                                    }}
                                    className="flex-1"
                                  >
                                    <Zap className="h-4 w-4 mr-2" />
                                    {t('TEMPLATEBUILDER_AISETTINGSDIALOG_AUTOMATCHKEY')}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        /* No available keys - show info message */
                        <div className="p-4 rounded-xl bg-muted/50 border border-dashed border-border">
                          <div className="flex items-start gap-3">
                            <Info className="h-5 w-5 text-globe-grey shrink-0 mt-0.5" />
                            <div className="space-y-2">
                              <p className="text-sm text-[hsl(var(--fg))]">
                                {t('TEMPLATEBUILDER_AISETTINGSDIALOG_NOKEYSTITLE')}
                              </p>
                              <p className="text-xs text-globe-grey">
                                {t('TEMPLATEBUILDER_AISETTINGSDIALOG_NOKEYSDESCRIPTION')}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Manual input for custom keys */}
                      <div className="space-y-2">
                        <Label className="text-xs text-globe-grey">
                          {t('TEMPLATEBUILDER_AISETTINGSDIALOG_CUSTOMKEYLABEL')}
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            placeholder={t('TEMPLATEBUILDER_AISETTINGSDIALOG_CUSTOMKEYPLACEHOLDER')}
                            className="font-mono text-sm"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={autoGenerateKey}
                            title={t('TEMPLATEBUILDER_AISETTINGSDIALOG_AUTOGENERATETITLE')}
                          >
                            <Zap className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Preview */}
                    {key && (
                      <div className="p-4 rounded-xl bg-lime-green/5 border border-[hsl(var(--lime-green))]/20">
                        <div className="flex items-start gap-3">
                          <div className="h-8 w-8 rounded-lg bg-lime-green/10 flex items-center justify-center shrink-0">
                            <Check className="h-4 w-4 text-poly-green" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-[hsl(var(--fg))]">
                              {t('TEMPLATEBUILDER_AISETTINGSDIALOG_PREVIEWTITLE')}
                            </p>
                            <p className="text-xs text-globe-grey">
                              {t('TEMPLATEBUILDER_AISETTINGSDIALOG_PREVIEWDESCRIPTION', {
                                value: key,
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Disabled state info */}
            {!enabled && (
              <div className="p-4 rounded-xl bg-muted/30 border border-dashed border-border">
                <p className="text-sm text-globe-grey text-center">
                  {t('TEMPLATEBUILDER_AISETTINGSDIALOG_DISABLEDINFO')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-end w-full gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              {t('TEMPLATEBUILDER_AISETTINGSDIALOG_CANCEL')}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              className="bg-selise-blue hover:bg-oxford-blue text-white"
            >
              {t('TEMPLATEBUILDER_AISETTINGSDIALOG_SAVE')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
